import React, { useState, useEffect, useMemo } from 'react'
import { priceFormat, showError, showSuccess, processAxiosError } from '../utils/helper'

const parseCroatianPrice = (price) => {
    if (typeof price === 'number') return price
    return parseFloat(String(price).replace(/\./g, '').replace(',', '.'))
}

const PartialStornoModal = ({ show, sale, onSuccess, onClose }) => {
    const [processing, setProcessing] = useState(false)
    const [returnItems, setReturnItems] = useState([])

    // Initialize return items when sale changes
    useEffect(() => {
        if (sale && sale.invoice_items) {
            setReturnItems(
                sale.invoice_items.map((item) => {
                    const originalQty = parseInt(item.item_quantity) || 0
                    const returnedQty = parseInt(item.item_quantity_return) || 0
                    const available = originalQty - returnedQty

                    return {
                        id: item.id,
                        name: item.item_name,
                        originalQty,
                        returnedQty,
                        available,
                        unitPrice: parseCroatianPrice(item.item_price_with_tax),
                        returnQty: 0,
                        checked: false,
                    }
                })
            )
        }
    }, [sale])

    const refundTotal = useMemo(() => {
        return returnItems.reduce((sum, item) => {
            if (item.checked && item.returnQty > 0) {
                return sum + item.unitPrice * item.returnQty
            }
            return sum
        }, 0)
    }, [returnItems])

    const hasSelection = useMemo(() => {
        return returnItems.some((item) => item.checked && item.returnQty > 0)
    }, [returnItems])

    const hasCardPayment = sale && sale.card_gateway_payment_id && !sale.card_refunded

    const updateItem = (index, changes) => {
        setReturnItems((prev) => {
            const updated = [...prev]
            updated[index] = { ...updated[index], ...changes }
            return updated
        })
    }

    const handleCheckChange = (index, checked) => {
        const item = returnItems[index]
        if (checked && item.returnQty === 0 && item.available > 0) {
            updateItem(index, { checked: true, returnQty: 1 })
        } else if (!checked) {
            updateItem(index, { checked: false, returnQty: 0 })
        } else {
            updateItem(index, { checked })
        }
    }

    const handleQtyChange = (index, value) => {
        const item = returnItems[index]
        let qty = parseInt(value) || 0
        if (qty < 0) qty = 0
        if (qty > item.available) qty = item.available

        updateItem(index, {
            returnQty: qty,
            checked: qty > 0,
        })
    }

    const handleSubmit = async () => {
        if (!hasSelection || processing) return

        setProcessing(true)

        const items = returnItems
            .filter((item) => item.checked && item.returnQty > 0)
            .map((item) => ({
                sale_item_id: item.id,
                quantity: item.returnQty,
            }))

        try {
            const { data } = await window.axios.post(`/sales/${sale.id}/partial-storno`, { items })

            if (data.success) {
                showSuccess('Djelomični storno uspješno izvršen.')

                if (data.card_refund_success === false && data.card_refund_error) {
                    showError('Greška pri povratu na karticu: ' + data.card_refund_error)
                } else if (data.card_refund_success === true) {
                    showSuccess('Povrat na karticu uspješno izvršen.')
                }

                if (onSuccess) onSuccess(data)
            } else {
                showError('Greška pri djelomičnom storniranju.')
            }
        } catch (error) {
            processAxiosError(error)
        } finally {
            setProcessing(false)
        }
    }

    if (!show || !sale) return null

    return (
        <div className="fixed w-full h-screen left-0 top-0 z-50 flex flex-wrap justify-center content-center p-24">
            <div onClick={onClose} className="fixed w-full h-screen left-0 top-0 z-0 bg-black bg-opacity-60"></div>
            <div className="w-full max-w-lg rounded-lg bg-white shadow-xl overflow-hidden z-10">
                <div className="p-6">
                    {/* Header */}
                    <div className="text-center mb-4">
                        <div className="flex justify-center mb-3">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Djelomični storno</h3>
                        <p className="text-gray-500 mt-1">Račun #{sale.id} ({sale.invoice_real_id})</p>
                    </div>

                    {sale.invoice_device_id && global?.invoiceDevice?.id && sale.invoice_device_id !== global.invoiceDevice.id && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-800">
                            Račun je izdan na blagajni <strong>{sale.invoice_space_name || sale.invoice_device_name}</strong>.
                            Storno se izdaje na ovoj blagajni, a vraćena roba ide na skladište ove poslovnice.
                        </div>
                    )}

                    {/* Item list */}
                    <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                        {returnItems.map((item, index) => (
                            <div key={item.id} className={`p-3 ${item.available === 0 ? 'opacity-50' : ''}`}>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        checked={item.checked}
                                        disabled={item.available === 0}
                                        onChange={(e) => handleCheckChange(index, e.target.checked)}
                                        className="checkbox checkbox-sm checkbox-error flex-shrink-0"
                                    />
                                    <div className="flex-grow min-w-0">
                                        <p className="font-medium text-sm text-gray-800 truncate">{item.name}</p>
                                        <div className="flex flex-wrap gap-x-3 text-xs text-gray-500 mt-0.5">
                                            <span>Kol: {item.originalQty}</span>
                                            {item.returnedQty > 0 && (
                                                <span className="text-red-500">Vraćeno: {item.returnedQty}</span>
                                            )}
                                            <span>Max: {item.available}</span>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            Cijena: {priceFormat(item.unitPrice)}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <label className="text-xs text-gray-500">Povrat:</label>
                                        <input
                                            type="number"
                                            min="0"
                                            max={item.available}
                                            value={item.returnQty}
                                            disabled={item.available === 0}
                                            onChange={(e) => handleQtyChange(index, e.target.value)}
                                            className="input input-bordered input-sm w-16 text-center"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Card payment info */}
                    {hasCardPayment && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                            <div className="flex items-start">
                                <svg className="w-5 h-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                                <div>
                                    <p className="text-sm font-semibold text-blue-800">Kartično plaćanje</p>
                                    <p className="text-sm text-blue-600 mt-0.5">Djelomični povrat na karticu kupca će se izvršiti automatski.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Refund total */}
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg text-center">
                        <span className="text-sm text-gray-600">Iznos povrata: </span>
                        <span className="text-lg font-bold text-gray-900">
                            {priceFormat(refundTotal)} {sale.invoice_currency_symbol || 'EUR'}
                        </span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mt-4">
                        <button
                            onClick={onClose}
                            className="flex-1 py-2.5 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
                        >
                            Odustani
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={!hasSelection || processing}
                            className="flex-1 py-2.5 px-4 bg-red-600 hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
                        >
                            {processing ? 'Procesuiranje...' : 'Storniraj odabrano'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PartialStornoModal
